import React from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

// Deliberately red/rose -- distinct from every series color (purple, green,
// amber) so the "today" highlight can't be mistaken for one of the bars
// themselves.
const TODAY_COLOR = '#E11D48';

function monthLabel(monthKey) {
  if (!monthKey) return '';
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function shiftMonth(monthKey, delta) {
  const [year, month] = monthKey.split('-').map(Number);
  const d = new Date(year, month - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-semibold text-gray-700">
        {row?.date || label}
        {row?.isToday && <span className="ml-1.5 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold text-indigo-700">Today</span>}
      </p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}: {entry.value}h
        </p>
      ))}
    </div>
  );
}

// Bolds and circles today's day-of-month on the x-axis so it's obvious even
// before hovering any bar.
function TodayTick({ x, y, payload, todayDayLabel }) {
  const isToday = payload?.value === todayDayLabel;
  return (
    <g transform={`translate(${x},${y})`}>
      {isToday && <circle cx={0} cy={10} r={11} fill={TODAY_COLOR} />}
      <text
        x={0}
        y={14}
        textAnchor="middle"
        fontSize={11}
        fontWeight={isToday ? 700 : 400}
        fill={isToday ? '#FFFFFF' : '#6B7280'}
      >
        {payload?.value}
      </text>
    </g>
  );
}

export default function WorkLogChart({ month, days, today, onMonthChange }) {
  const todayDayLabel = today && month === today.slice(0, 7) ? String(Number(today.slice(8, 10))) : null;
  const chartData = days.map((d) => ({ ...d, isToday: d.date === today }));

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-bold text-gray-900">Monthly Working Hours</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onMonthChange(shiftMonth(month, -1))}
            className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
            aria-label="Previous month"
          >
            ‹
          </button>
          <span className="min-w-[140px] text-center text-sm font-semibold text-gray-700">{monthLabel(month)}</span>
          <button
            type="button"
            onClick={() => onMonthChange(shiftMonth(month, 1))}
            className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm font-semibold text-gray-600 hover:bg-gray-50"
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="dayLabel"
            stroke="#6B7280"
            fontSize={11}
            interval={0}
            tick={<TodayTick todayDayLabel={todayDayLabel} />}
          />
          <YAxis stroke="#6B7280" fontSize={11} label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fill: '#6B7280', fontSize: 11 } }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          {todayDayLabel && (
            <ReferenceLine x={todayDayLabel} stroke={TODAY_COLOR} strokeDasharray="4 4" strokeWidth={1.5} />
          )}
          <Bar dataKey="expectedHours" fill="#8B5CF6" name="Expected hours" radius={[4, 4, 0, 0]}>
            {chartData.map((d) => (
              <Cell key={d.date} stroke={d.isToday ? TODAY_COLOR : 'none'} strokeWidth={d.isToday ? 2 : 0} />
            ))}
          </Bar>
          <Bar dataKey="completedLogHours" stackId="actual" fill="#10B981" name="Logged work (completed)">
            {chartData.map((d) => (
              <Cell key={d.date} stroke={d.isToday ? TODAY_COLOR : 'none'} strokeWidth={d.isToday ? 2 : 0} />
            ))}
          </Bar>
          <Bar dataKey="classHours" stackId="actual" fill="#F59E0B" name="Scheduled classes">
            {chartData.map((d) => (
              <Cell key={d.date} stroke={d.isToday ? TODAY_COLOR : 'none'} strokeWidth={d.isToday ? 2 : 0} />
            ))}
          </Bar>
          <Bar dataKey="eventHours" stackId="actual" fill="#3B82F6" name="Events" radius={[4, 4, 0, 0]}>
            {chartData.map((d) => (
              <Cell key={d.date} stroke={d.isToday ? TODAY_COLOR : 'none'} strokeWidth={d.isToday ? 2 : 0} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {todayDayLabel && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: TODAY_COLOR }} />
          Today&rsquo;s bars are outlined and the day is highlighted on the axis.
        </p>
      )}
    </div>
  );
}
